import { OAuth2ErrorCode, OAuth2ErrorResponse } from '../types';

/**
 * OAuth2 Error Handler
 * Provides standardized error handling for OAuth2 flows
 */

export class OAuth2Error extends Error {
    public readonly code: OAuth2ErrorCode;
    public readonly description?: string;
    public readonly uri?: string;
    public readonly state?: string;

    constructor(
        code: OAuth2ErrorCode,
        description?: string,
        state?: string,
        uri?: string
    ) {
        super(description || code);
        this.name = 'OAuth2Error';
        this.code = code;
        this.description = description;
        this.state = state;
        this.uri = uri;
    }

    toErrorResponse(): OAuth2ErrorResponse {
        return {
            error: this.code,
            error_description: this.description,
            error_uri: this.uri,
            state: this.state,
        };
    }

    toRedirectUrl(redirectUri: string): string {
        const params = new URLSearchParams();
        params.set('error', this.code);
        
        if (this.description) {
            params.set('error_description', this.description);
        }
        
        if (this.uri) {
            params.set('error_uri', this.uri);
        }
        
        if (this.state) {
            params.set('state', this.state);
        }

        return `${redirectUri}?${params.toString()}`;
    }
}

// Predefined OAuth2 errors
export const OAuth2Errors = {
    invalidRequest: (description?: string, state?: string) =>
        new OAuth2Error('invalid_request', description, state),
    
    unauthorizedClient: (description?: string, state?: string) =>
        new OAuth2Error('unauthorized_client', description, state),
    
    accessDenied: (description?: string, state?: string) =>
        new OAuth2Error('access_denied', description, state),
    
    unsupportedResponseType: (description?: string, state?: string) =>
        new OAuth2Error('unsupported_response_type', description, state),
    
    invalidScope: (description?: string, state?: string) =>
        new OAuth2Error('invalid_scope', description, state),
    
    serverError: (description?: string, state?: string) =>
        new OAuth2Error('server_error', description, state),
    
    temporarilyUnavailable: (description?: string, state?: string) =>
        new OAuth2Error('temporarily_unavailable', description, state),
};

// Error mapping for common scenarios
export const mapErrorToOAuth2Error = (
    error: unknown,
    state?: string
): OAuth2Error => {
    if (error instanceof OAuth2Error) {
        return error;
    }

    if (error instanceof Error) {
        const message = error.message.toLowerCase();
        
        if (message.includes('invalid redirect')) {
            return OAuth2Errors.invalidRequest('Invalid redirect URI', state);
        }
        
        if (message.includes('client') || message.includes('account')) {
            return OAuth2Errors.unauthorizedClient('Client not found or unauthorized', state);
        }
        
        if (message.includes('session') || message.includes('expired')) {
            return OAuth2Errors.accessDenied('Session expired or invalid', state);
        }
        
        if (message.includes('scope')) {
            return OAuth2Errors.invalidScope('Invalid or unsupported scope', state);
        }
    }

    return OAuth2Errors.serverError('Internal server error', state);
};